import type { SiteData, BracketData } from "../types";
import { flipArray, itemLast } from "./utils";

function setSeeds32(
  siteData: SiteData,
  brackets: BracketData,
  type: "winners" | "losers",
): SiteData {
  let seedCount = 1;
  const arr = brackets[type];
  for (let x = 0; x < arr.length; x += 2) {
    const songValues = [arr[x]!, arr[x + 1]!];
    siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
      "seed" + seedCount
    ] = songValues;
    seedCount += 1;
  }
  siteData.bracketsModeSettings.current2Seeds =
    siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
      "seed" + siteData.bracketsModeSettings.currentSeed
    ]!;
  return siteData;
}

export interface BracketScenarioResult {
  siteData: SiteData;
  brackets: BracketData;
  isFinal: boolean;
}

export function bracketScenario32(
  inputSiteData: SiteData,
  inputBrackets: BracketData,
  winner: number,
  loser: number,
): BracketScenarioResult {
  const siteData = structuredClone(inputSiteData);
  const brackets = structuredClone(inputBrackets);

  const { currentSeed, gameResults, currentRound } =
    siteData.bracketsModeSettings;
  const winnersRoundsSet1 = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
  ];
  const losersRoundsSet1 = [17, 18, 19, 20, 21, 22, 23, 24];
  const winnersRoundSet2 = [25, 26, 27, 28, 29, 30, 31, 32];
  const losersRoundsSet2 = [33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44];
  const winnersRoundSet3 = [45, 46, 47, 48];
  const losersRoundsSet3 = [49, 50, 51, 52, 53, 54];
  const winnersRoundSet4 = [55, 56];
  const losersRoundsSet4 = [57, 58, 59];

  delete siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
    "seed" + currentSeed
  ];

  // 1-16 (Winners)
  if (winnersRoundsSet1.includes(currentRound)) {
    brackets.winners.push(winner);
    brackets.losers.push(loser);
    if (currentRound === itemLast(winnersRoundsSet1, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds32(siteData, brackets, "losers");
    } else {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
  }

  // 17-24 (Losers)
  if (losersRoundsSet1.includes(currentRound)) {
    gameResults.push(loser);
    const indexOfLoser = brackets.losers.indexOf(loser);
    brackets.losers.splice(indexOfLoser, 1, -1);
    if (currentRound === itemLast(losersRoundsSet1, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds32(siteData, brackets, "winners");
      const keepSecondHalf = brackets.losers.slice(8, 16);
      const flipFirstHalf = brackets.losers.slice(0, 8);
      brackets.losers = [...keepSecondHalf, ...flipFirstHalf];
    } else {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
    if (!brackets.losers.includes(winner)) {
      brackets.losers.push(winner);
    }
  }

  // 25-32 (Winners)
  if (winnersRoundSet2.includes(currentRound)) {
    if (brackets.winners.includes(loser)) {
      const loserIndex = brackets.winners.indexOf(loser);
      brackets.winners.splice(loserIndex, 1);
    }
    const negativeValue = brackets.losers.indexOf(-1);
    brackets.losers.splice(negativeValue, 1, loser);
    if (currentRound === itemLast(winnersRoundSet2, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds32(siteData, brackets, "losers");
    } else {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
  }

  // 33-44 (Losers)
  if (losersRoundsSet2.includes(currentRound)) {
    gameResults.push(loser);
    const indexOfLoser = brackets.losers.indexOf(loser);
    if (
      currentRound === itemLast(losersRoundsSet2, 1) ||
      currentRound === itemLast(losersRoundsSet2, 2) ||
      currentRound === itemLast(losersRoundsSet2, 3) ||
      currentRound === itemLast(losersRoundsSet2, 4)
    ) {
      brackets.losers.splice(indexOfLoser, 1, -1);
    } else {
      brackets.losers.splice(indexOfLoser, 1);
    }
    if (currentRound === itemLast(losersRoundsSet2, 5)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds32(siteData, brackets, "losers");
    }
    if (currentRound === itemLast(losersRoundsSet2, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds32(siteData, brackets, "winners");
      const keepSecondHalf = brackets.losers.slice(4, 8);
      const flipFirstHalf = brackets.losers.slice(0, 4);
      brackets.losers = [...keepSecondHalf, ...flipFirstHalf];
    } else if (currentRound !== itemLast(losersRoundsSet2, 5)) {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
    if (!brackets.losers.includes(winner)) {
      brackets.losers.push(winner);
    }
  }

  // 45-48 (Winners)
  if (winnersRoundSet3.includes(currentRound)) {
    if (brackets.winners.includes(loser)) {
      const loserIndex = brackets.winners.indexOf(loser);
      brackets.winners.splice(loserIndex, 1);
    }
    const negativeValue = brackets.losers.indexOf(-1);
    brackets.losers.splice(negativeValue, 1, loser);
    if (currentRound === itemLast(winnersRoundSet3, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds32(siteData, brackets, "losers");
    } else {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
  }

  // 49-54 (Losers)
  if (losersRoundsSet3.includes(currentRound)) {
    gameResults.push(loser);
    const indexOfLoser = brackets.losers.indexOf(loser);
    if (
      currentRound !== itemLast(losersRoundsSet3, 1) &&
      currentRound !== itemLast(losersRoundsSet3, 2)
    ) {
      brackets.losers.splice(indexOfLoser, 1);
    } else {
      brackets.losers.splice(indexOfLoser, 1, -1);
    }
    if (currentRound === itemLast(losersRoundsSet3, 3)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds32(siteData, brackets, "losers");
    }
    if (currentRound === itemLast(losersRoundsSet3, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      brackets.losers = flipArray(brackets.losers);
      setSeeds32(siteData, brackets, "winners");
    } else if (currentRound !== itemLast(losersRoundsSet3, 3)) {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
    if (!brackets.losers.includes(winner)) {
      brackets.losers.push(winner);
    }
  }

  // 55-56 (Winners)
  if (winnersRoundSet4.includes(currentRound)) {
    if (brackets.winners.includes(loser)) {
      const loserIndex = brackets.winners.indexOf(loser);
      brackets.winners.splice(loserIndex, 1);
    }
    const negativeValue = brackets.losers.indexOf(-1);
    brackets.losers.splice(negativeValue, 1, loser);
    if (currentRound === itemLast(winnersRoundSet4, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds32(siteData, brackets, "losers");
    } else {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
  }

  // 57-59 (Losers)
  if (losersRoundsSet4.includes(currentRound)) {
    gameResults.push(loser);
    const indexOfLoser = brackets.losers.indexOf(loser);
    if (currentRound !== itemLast(losersRoundsSet4, 1)) {
      brackets.losers.splice(indexOfLoser, 1);
    } else {
      brackets.losers.splice(indexOfLoser, 1, -1);
    }
    if (currentRound === itemLast(losersRoundsSet4, 2)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds32(siteData, brackets, "losers");
    }
    if (currentRound === itemLast(losersRoundsSet4, 1)) {
      siteData.bracketsModeSettings.currentSeed = 1;
      setSeeds32(siteData, brackets, "winners");
    } else if (currentRound !== itemLast(losersRoundsSet4, 2)) {
      siteData.bracketsModeSettings.currentSeed += 1;
      siteData.bracketsModeSettings.current2Seeds =
        siteData.bracketsModeSettings.gameSeedSetup.activeSeeds[
          "seed" + (currentSeed + 1)
        ]!;
    }
    if (!brackets.losers.includes(winner)) {
      brackets.losers.push(winner);
    }
  }

  // 60 (Winners)
  if (currentRound === 60) {
    const indexOfLoserInWinners = brackets.winners.indexOf(loser);
    brackets.winners.splice(indexOfLoserInWinners, 1);
    const indexOfNeg = brackets.losers.indexOf(-1);
    brackets.losers.splice(indexOfNeg, 1, loser);
    siteData.bracketsModeSettings.currentSeed = 1;
    setSeeds32(siteData, brackets, "losers");
    if (!brackets.winners.includes(winner)) {
      brackets.winners.push(winner);
    }
  }

  // 61 (Losers)
  if (currentRound === 61) {
    gameResults.push(loser);
    brackets.losers = [];
    brackets.winners.push(winner);
    siteData.bracketsModeSettings.currentSeed = 1;
    setSeeds32(siteData, brackets, "winners");
  }

  // 62 (Winners) — Final round
  if (currentRound === 62) {
    return { siteData, brackets, isFinal: true };
  }

  siteData.bracketsModeSettings.currentRound += 1;
  return { siteData, brackets, isFinal: false };
}
